import {
  Grid,
  Button,
  FormLabel,
  Autocomplete,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import BasicTextField from "./formElements/BasicTextField";
import CheckBoxSection from "./formElements/CheckBoxSection";
import SelectInputField from "./formElements/SelectInputField";
import DateField from "./formElements/DateField";
import { useEffect, useState } from "react";
import SplitLayout from "./formLayouts/SplitLayout";
import { useTheme } from "@mui/material/styles";
import useButtonStyles from "../../styles/useButtonStyles";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import useFormStyles from "../../styles/useFormStyles";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import BasicModal from "../ui/BasicModal";
import Loader from "../ui/Loader";
import MultipleSelectInputField from "./formElements/MultipleSelectInputField";
import { countries } from "./formLayouts/countries";
import { interests } from "./formLayouts/interests";
import { getFeaturePermissions } from "../../util/permissions";
import formatDates from "../../util/dateFormat";
import { countryCodes } from "./formLayouts/countryCodes";
import CountryCodeSelect from "./formElements/CountryCodeSelect";
import CloseIcon from "@mui/icons-material/Close";
import { activityLogRefresh } from "../member/MemberDetailsHeaderNew";
import useGetMemberDetails, {
  memberDetailsRefresh,
} from "../../Hooks/member/useGetMemberDetails";
import { filterConstants } from "../../constants/AppConstants";
export interface UpdateMemberFormProps {
  initialFormData: UpdateMemberFormDataUpdated;
  type: "Update";
  closeUpdate?: Function;
}

export interface UpdateMemberFormDataUpdated {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  gender: string;
  dob_date: string;
  zip: string;
  country_of_residence: string;
  storecountrycode: string;
  customer_status: string;
  shipping_address: string;
  delivery_city: string;
  salutation: string;
  subscr_for_news: boolean;
  subscr_for_mkt: boolean;
  email_marketing: boolean;
  heard_about_us: string;
  occupation: string;
  unit_no: string;
  street_address: string;
  is_birthday_month: boolean;
  source_tag: string;
  member_type: string;
  password: string;
  source?: string;
  loyalty_type?: string;
  interest: string;
  isInternalStaff: string;
  currentSlab: string;
  isinternalstaffdiamo: string;
  isinternstaffgold: boolean | string;
  isinternalsatffquit: boolean | string;
  emailNotification: false;
  pushNotification: false;
  other_heard_about_us: string;
  corporate_email: string;
  personal_email: string;
  access_id: string;
  iuNumber1: string;
  iuNumber2: string;
  licensePlate1: string;
  licensePlate2: string;
  vehicleDetails: any;
  mobileWOCc: string;
}
const convertToBoolean = (value: string | boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  return value === "true";
};
const UpdateMember: React.FC<UpdateMemberFormProps> = (
  props: UpdateMemberFormProps,
) => {
  const { initialFormData, type, closeUpdate } = props;
  const [memberFormState, setMemberFormState] =
    useState<UpdateMemberFormDataUpdated>(initialFormData);
  const { getMemberDetails } = useGetMemberDetails("");
  const theme = useTheme();
  const formStyles = useFormStyles();
  const buttonStyles = useButtonStyles();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [countryCode, setCountryCode] = useState<string>("");
  const [messageToSuccess, setMessageToSuccess] = useState(
    "Member Updated Successfully!",
  );
  const [countryOptions, setCountryOptions] = useState<string[]>(countries);
  const otherHeardAboutUs = initialFormData.heard_about_us;
  const [data, setData] = useState([]);
  const memberDetails = useAppSelector(
    (state: any) => state.persistReducers.getMemberList?.membersList,
  );
  const permissions = useAppSelector(
    (state: any) => state.persistReducers.login.permissions,
  );

  // Resolve stored CC values: "65", "+65", "SG (+65)", or "SG"
  function findCountryCode(code: string) {
    const dialCode = code?.match(/[0-9]+/)?.[0];
    return countryCodes.find((country) =>
      dialCode ? country.code === dialCode : country.abbreviation === code,
    );
  }

  // CC dropdown display. Blank/unknown values default to SG (+65)
  function appendAbbreviationToCode(code: string) {
    const countryCodeObj = findCountryCode(code);

    if (countryCodeObj) {
      return `${countryCodeObj.abbreviation} (+${countryCodeObj.code})`;
    }

    return "SG (+65)";
  }

  // Payload storecountrycode should be abbreviation only, e.g. "SG"
  function getCountryAbbreviation(code: string) {
    const countryCodeObj = findCountryCode(code);

    return countryCodeObj ? countryCodeObj.abbreviation : "";
  }

  // Existing mobile cannot be cleared to blank
  const isMobileCleared =
    Boolean(initialFormData.mobileWOCc) && !memberFormState.mobileWOCc;

  const isCountryOfResidenceRequired =
    memberFormState?.storecountrycode !== "+65";
  const isPostalCodeRequired =
    memberFormState?.storecountrycode === "SG (+65)" ||
    memberFormState?.country_of_residence === "Singapore";
  const formatDate = (joinDate: Date) => {
    return (
      joinDate.getFullYear() +
      "-" +
      joinDate.getMonth() +
      "-" +
      joinDate.getDate()
    );
  };
  const formatDOBDate = (date: string) => {
    const dobDate = new Date(date);
    return (
      dobDate.getFullYear() +
      "-" +
      (dobDate.getMonth() + 1) +
      "-" +
      dobDate.getDate()
    );
  };

  const columns = [
    {
      label: "Activity Title",
      key: "activityTitle",
    },
    {
      label: "Created by",
      key: "createdBy",
    },
    {
      label: "Created Date",
      key: "createdAt",
    },
  ];

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL +
          APIConstants.getMemberLogs +
          "?email=" +
          memberFormState.email
        }`,
      );
      const mapedData = response.data.result.map((item: any, index: any) => ({
        createdAt: formatDates(item.createdAt),
        activityTitle: item.activity,
        createdBy: item.createdBy ? item.createdBy : "-",
      }));

      mapedData.sort(
        (
          a: { createdAt: string | number | Date },
          b: { createdAt: string | number | Date },
        ) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      setData(mapedData);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await uploadData();
    fetchData();
  };

  useEffect(() => {
    if (
      ![
        "Advertisement",
        "I work around/at Suntec City",
        "Social Media",
        "Through friends and family",
        "yuu",
      ].includes(memberFormState?.heard_about_us)
    ) {
      setMemberFormState((prev: any) => {
        const trimmedValue = prev?.heard_about_us?.trim();
        return { ...prev, other_heard_about_us: trimmedValue };
      });
    }
  }, []);

  useEffect(() => {
    if (memberFormState.isInternalStaff === "Gold") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isinternstaffgold: "YES",
      }));
    } else if (memberFormState.isInternalStaff === "Diamond") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isinternalstaffdiamo: "YES",
      }));
    } else if (memberFormState.isInternalStaff === "Quit") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isinternalsatffquit: "YES",
      }));
    }
  }, [memberFormState.isInternalStaff]);
  useEffect(() => {
    if (memberFormState.isinternstaffgold === "YES") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isInternalStaff: "Gold",
      }));
    } else if (memberFormState.isinternalstaffdiamo === "YES") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isInternalStaff: "Diamond",
      }));
    } else if (memberFormState.isinternalsatffquit === "YES") {
      setMemberFormState((prevState) => ({
        ...prevState,
        isInternalStaff: "Quit",
      }));
    }
  }, [
    memberFormState.isinternstaffgold,
    memberFormState.isinternalstaffdiamo,
    memberFormState.isinternalsatffquit,
  ]);

  function getMaxDate() {
    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - 18);
    return currentDate.toISOString().split("T")[0];
  }
  const countryCodeMatch = memberFormState?.storecountrycode?.match(/([0-9]+)/);
  const countryCodeAbbreviation = countryCodeMatch ? countryCodeMatch[0] : "";
  const uploadData = async () => {
    setLoading(true);

    try {
      const payload: any = { ...memberFormState };
      payload.first_name = memberFormState.first_name.trim();
      payload.last_name = memberFormState.last_name.trim();
      payload.source = "INSTORE";
      payload.loyaltyType = "Loyalty";
      payload.dob_date = formatDOBDate(memberFormState?.dob_date);
      payload.password = "Abcd1234@";
      payload.unit_no = "565";
      payload.heard_about_us =
        memberFormState?.heard_about_us === "Other"
          ? memberFormState?.other_heard_about_us
          : memberFormState.heard_about_us;
      if (payload.tier === "Gold") {
        payload.isinternstaffgold = "YES";
        payload.currentSlab = "Gold";
        payload.isinternalstaffdiamo = "NO";
      } else if (payload.tier === "Diamond") {
        payload.isinternalstaffdiamo = "YES";
        payload.currentSlab = "Diamond";
        payload.isinternstaffgold = "NO";
      } else if (payload.tier === "Quit") {
        payload.isinternalsatffquit = "YES";
        payload.isinternalstaffdiamo = "NO";
        payload.isinternstaffgold = "NO";
      } else {
        payload.isinternstaffgold = "NO";
        payload.isinternalstaffdiamo = "NO";
        payload.isinternalsatffquit = "NO";
      }
      const payloadKeys = Object.keys(payload);
      let vehicle1: any = {};
      let vehicle2: any = {};
      payloadKeys.map((key: any) => {
        if (key === "iuNumber1" && payload[key] !== "") {
          vehicle1["iuNumber"] = payload[key];
        } else if (key === "iuNumber2" && payload[key] !== "") {
          vehicle2["iuNumber"] = payload[key];
        } else if (key === "licensePlate1" && payload[key] !== "") {
          vehicle1["licensePlate"] = payload[key];
        } else if (key === "licensePlate2" && payload[key] !== "") {
          vehicle2["licensePlate"] = payload[key];
        }
      });
      const vehicleDetails = [];

      const isVehicle1Changed = Object.keys(vehicle1).some((key) => {
        if (
          initialFormData.vehicleDetails.length &&
          initialFormData.vehicleDetails[0][key]
        ) {
          return vehicle1[key] !== initialFormData.vehicleDetails[0][key];
        } else return initialFormData.vehicleDetails.length === 0;
      });

      if (Object.keys(vehicle1).length > 0 && isVehicle1Changed) {
        vehicleDetails.push(vehicle1);
      }

      const isVehicle2Changed = Object.keys(vehicle1).some((key) => {
        if (
          initialFormData.vehicleDetails.length > 1 &&
          initialFormData.vehicleDetails[1][key]
        ) {
          return vehicle2[key] !== initialFormData.vehicleDetails[1][key];
        } else return initialFormData.vehicleDetails.length === 1;
      });

      if (Object.keys(vehicle2).length > 0 && isVehicle2Changed) {
        vehicleDetails.push(vehicle2);
      }
      if (vehicleDetails.length) {
        payload.vehicleDetails = vehicleDetails;
      }

      if (initialFormData.vehicleDetails.length === 2) {
        delete payload.vehicleDetails;
      }

      // delete payload.iuNumber1;
      // delete payload.iuNumber2;
      // delete payload.licensePlate1;
      // delete payload.licensePlate2;
      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === initialFormData[key as keyof typeof initialFormData]
        ) {
          if (key !== "email") {
            delete payload[key];
          }
        }
      });

      // Mobile payload: number without country code; storecountrycode as "SG" not "SG (+65)".
      // Default UI CC (+65) is ignored unless mobile or an existing CC actually changed.
      delete payload.mobile;
      delete payload.storecountrycode;
      const selectedAbbreviation = getCountryAbbreviation(
        memberFormState.storecountrycode,
      );
      const initialAbbreviation = getCountryAbbreviation(
        initialFormData.storecountrycode,
      );
      if (memberFormState.mobileWOCc !== initialFormData.mobileWOCc) {
        payload.mobile = memberFormState.mobileWOCc;
        payload.storecountrycode = selectedAbbreviation;
      } else if (
        initialAbbreviation &&
        selectedAbbreviation !== initialAbbreviation
      ) {
        payload.storecountrycode = selectedAbbreviation;
      }

      const axiosResponse = await axios.put(
        process.env.REACT_APP_API_URL + APIConstants.getCustomer,
        payload,
      );
      if (axiosResponse.data.data.status) {
        // Await the getMemberDetails to ensure Redux is updated
        await getMemberDetails(memberDetails.id);
        // Force a small delay to ensure Redux state is fully updated
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      setUploadError(false);
      const message = axiosResponse?.data?.data?.message;
      setMessageToSuccess(message || "User successfully updated");
      setShowModal(true);
      if (activityLogRefresh || memberDetailsRefresh) activityLogRefresh();
    } catch (error: any) {
      console.error(error);
      const message = error?.response?.data?.message as string;
      setMessageToSuccess(message || "Something went wrong");
      setUploadError(true);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const searchComponent = (
    <Autocomplete
      size="small"
      value={memberFormState.country_of_residence}
      onChange={(e, newValue) => {
        const newCountryValue = newValue ? newValue : "";
        setMemberFormState((prevState) => ({
          ...prevState,
          country_of_residence: newCountryValue,
        }));
      }}
      options={countryOptions}
      getOptionLabel={(option) => option}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          required={isCountryOfResidenceRequired}
        />
      )}
    />
  );
  const checkDisableMobileSubmit = () => {
    // Mobile validation - if mobile has value, check it doesn't start with 65 and is not all zeros

    // Block save if an existing mobile number was cleared
    if (isMobileCleared) {
      return true;
    }

    if (
      memberFormState.country_of_residence === "Singapore" ||
      memberFormState.storecountrycode === "SG (+65)"
    ) {
      const mobileRegex = /^(?!65)(?:\+?\d{8,15}|\d{16})$/;
      const isAllZeros = /^0+$/.test(memberFormState.mobileWOCc);
      const isMobileValid =
        !memberFormState.mobileWOCc ||
        (mobileRegex.test(memberFormState.mobileWOCc) && !isAllZeros);

      const isFormValid = isMobileValid;

      return !isFormValid;
    } else {
      const isAllZeros = /^0+$/.test(memberFormState.mobileWOCc);
      const isFormValid = !isAllZeros;

      return !isFormValid;
    }
  };

  return (
    <Grid
      container
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      width={"100%"}
      height={"100%"}
    >
      {/* <Card
        raised={true}
        variant="outlined"
        sx={{
          mx: { xs: 0, md: 0, lg: 8, xl: 8 },
          borderRadius: "8px",
          p: 4,
          my: 2,
        }}
      > */}
      <Grid p={3} px={4} height={"100%"}>
        <Grid
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          px={3}
        >
          <Typography
            sx={{
              fontFamily: theme.font?.fontFamily.primary,
              fontSize: theme.font?.fontSize.large,
              fontWeight: theme.font?.fontWeight.thick,
            }}
          >
            Update Profile
          </Typography>
          <IconButton
            aria-label="delete"
            onClick={closeUpdate ? () => closeUpdate(false) : () => {}}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid component={"form"} onSubmit={submitHandler} container item py={3}>
          <Grid
            container
            item
            width={"100%"}
            pb={2}
            borderBottom={theme.border?.smallGrey}
          >
            <Grid item width={"100%"}>
              <SplitLayout columns={[4, 4, 4]}>
                <BasicTextField
                  setState={setMemberFormState}
                  placeholder=""
                  label="First Name *"
                  stateKey="first_name"
                  variant="outlined"
                  required={true}
                  disableSubmit={setDisableSubmit}
                  defaultValue={memberFormState?.first_name}
                />
                <BasicTextField
                  setState={setMemberFormState}
                  placeholder=""
                  label="Last Name *"
                  stateKey="last_name"
                  variant="outlined"
                  required={true}
                  disableSubmit={setDisableSubmit}
                  defaultValue={memberFormState?.last_name}
                />
                <BasicTextField
                  setState={setMemberFormState}
                  placeholder=""
                  stateKey="email"
                  variant="outlined"
                  label="Email *"
                  align="left"
                  readonly
                  required={true}
                  disableSubmit={setDisableSubmit}
                  defaultValue={memberFormState?.email}
                />
              </SplitLayout>
            </Grid>
            <Grid item width={"100%"}>
              <SplitLayout columns={[4, 4, 4]}>
                <DateField
                  setState={setMemberFormState}
                  stateKey="dob_date"
                  label="Date of Birth *"
                  disableSubmit={setDisableSubmit}
                  maxValue={getMaxDate()}
                  defaultValue={memberFormState?.dob_date}
                />
                <SelectInputField
                  defaultValue={memberFormState?.gender}
                  setState={setMemberFormState}
                  stateKey="gender"
                  label="Gender *"
                  options={["Male", "Female", "Other"]}
                  align="left"
                />
                <FormLabel
                  sx={{
                    ...formStyles.formLabel,
                    textAlign: "left",
                    color: "#252836",
                  }}
                >
                  Postal Code{isPostalCodeRequired ? " *" : ""}
                  <BasicTextField
                    setState={setMemberFormState}
                    stateKey="zip"
                    variant="outlined"
                    placeholder=""
                    type="number"
                    required={
                      memberFormState.storecountrycode === "SG (+65)" ||
                      memberFormState?.country_of_residence === "Singapore"
                    }
                    defaultValue={memberFormState?.zip}
                    disableSubmit={setDisableSubmit}
                  />
                </FormLabel>
              </SplitLayout>
            </Grid>

            <Grid item width={"100%"}>
              <SplitLayout columns={[1.8, 2.2, 4]}>
                <CountryCodeSelect
                  label="CC"
                  // Default blank CC to SG (+65) in the dropdown
                  defaultValue={appendAbbreviationToCode(
                    memberFormState?.storecountrycode,
                  )}
                  stateKey="storecountrycode"
                  align="left"
                  setState={setMemberFormState}
                />

                <BasicTextField
                  setState={setMemberFormState}
                  stateKey="mobileWOCc"
                  label="Mobile"
                  variant="outlined"
                  align="left"
                  type="number"
                  defaultValue={
                    memberFormState?.mobileWOCc
                      ? memberFormState.mobileWOCc
                      : ""
                  }
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  isSingapore={
                    memberFormState.country_of_residence === "Singapore" ||
                    memberFormState.storecountrycode === "SG (+65)"
                      ? true
                      : false
                  }
                  applyStrictMobileValidation={true}
                  // Show error when user tries to save an existing mobile as blank
                  errorMessage={
                    isMobileCleared ? "Please update a valid mobile number" : ""
                  }
                  helperTextInfo="*Enter valid mobile number without country code"
                  placeholder=""
                  disableSubmit={setDisableSubmit}
                />
                <FormLabel
                  sx={{
                    ...formStyles.formLabel,
                    textAlign: "left",
                    color: "#252836",
                  }}
                >
                  Country {isCountryOfResidenceRequired ? " *" : ""}
                  <SelectInputField
                    defaultValue={memberFormState?.country_of_residence}
                    setState={setMemberFormState}
                    stateKey="country_of_residence"
                    options={countries}
                    searchComponent={searchComponent}
                    align="left"
                    required={isCountryOfResidenceRequired}
                  />
                </FormLabel>
              </SplitLayout>
            </Grid>
          </Grid>
          <Grid
            container
            item
            width={"100%"}
            borderBottom={theme.border?.smallGrey}
            pt={2}
            pb={2}
          >
            <SplitLayout columns={[4, 4, 4]}>
              <SelectInputField
                defaultValue={
                  memberFormState.isinternstaffgold === "YES"
                    ? "Gold"
                    : memberFormState.isinternalstaffdiamo === "YES"
                      ? "Diamond"
                      : memberFormState.isinternalsatffquit === "YES"
                        ? "Quit"
                        : "Select"
                }
                setState={(newValue: any) => {
                  setMemberFormState(newValue);
                  if (newValue === "Gold") {
                    setMemberFormState((prevState) => ({
                      ...prevState,
                      isinternstaffgold: "YES",
                      isinternalstaffdiamo: "NO",
                      isinternalsatffquit: "NO",
                    }));
                  }
                  if (newValue === "Diamond") {
                    setMemberFormState((prevState) => ({
                      ...prevState,
                      isinternalstaffdiamo: "YES",
                      isinternstaffgold: "NO",
                      isinternalsatffquit: "NO",
                    }));
                  }
                  if (newValue === "Quit") {
                    setMemberFormState((prevState) => ({
                      ...prevState,
                      isinternalstaffdiamo: "NO",
                      isinternstaffgold: "NO",
                      isinternalsatffquit: "YES",
                    }));
                  }
                }}
                stateKey="tier"
                label="Is Internal Staff"
                options={["Select", "Gold", "Diamond", "Quit"]}
                align="left"
              />
            </SplitLayout>
          </Grid>
          <Grid
            container
            item
            width={"100%"}
            borderBottom={theme.border?.smallGrey}
            pt={2}
            pb={2}
          >
            <Grid item width={"100%"}>
              <SplitLayout columns={[4, 8]}>
                <BasicTextField
                  setState={setMemberFormState}
                  stateKey="memberType"
                  label="Member Type"
                  variant="outlined"
                  readonly
                  placeholder="Shopper"
                  defaultValue={memberFormState?.member_type}
                />
                {memberFormState.member_type === "Corporate" ? (
                  <Grid item width={"100%"}>
                    <SplitLayout columns={[6, 6]}>
                      {memberFormState.email !==
                      memberFormState.corporate_email ? (
                        <>
                          <BasicTextField
                            setState={setMemberFormState}
                            stateKey="corporate_email"
                            label="Corporate Email"
                            variant="outlined"
                            placeholder=""
                            readonly
                            defaultValue={memberFormState?.corporate_email}
                          />
                        </>
                      ) : (
                        <>
                          {memberFormState.personal_email &&
                            memberFormState.personal_email.length > 0 && (
                              <BasicTextField
                                setState={setMemberFormState}
                                stateKey="personal_email"
                                label="Personal Email"
                                variant="outlined"
                                placeholder=""
                                readonly
                                defaultValue={memberFormState?.personal_email}
                              />
                            )}
                        </>
                      )}
                      <BasicTextField
                        setState={setMemberFormState}
                        stateKey="access_id"
                        label="Access ID"
                        variant="outlined"
                        placeholder=""
                        readonly
                        defaultValue={memberFormState?.access_id}
                      />
                    </SplitLayout>
                  </Grid>
                ) : null}
              </SplitLayout>
            </Grid>
            <Grid item width={"100%"}>
              <SplitLayout columns={[4, 4, 4]}>
                <SelectInputField
                  defaultValue={memberFormState?.customer_status}
                  setState={setMemberFormState}
                  stateKey="customer_status"
                  label="Member Status"
                  options={[filterConstants.Active, filterConstants.Inactive]}
                  align="left"
                />
                <MultipleSelectInputField
                  defaultValue={memberFormState?.interest}
                  setState={setMemberFormState}
                  label="Interest"
                  stateKey="interest"
                  options={interests}
                  align="left"
                />
              </SplitLayout>
            </Grid>
            <Grid item width={"100%"}>
              <SplitLayout columns={[4, 4, 4]}>
                <SelectInputField
                  setState={setMemberFormState}
                  label="Where did you hear about us?"
                  stateKey="heard_about_us"
                  options={[
                    "yuu",
                    "I work around/at Suntec City",
                    "Social Media",
                    "Through friends and family",
                    "Other",
                  ]}
                  defaultValue={
                    memberFormState.other_heard_about_us
                      ? "Other"
                      : memberFormState?.heard_about_us
                  }
                  align="left"
                />
                {![
                  "Advertisement",
                  "I work around/at Suntec City",
                  "Social Media",
                  "Through friends and family",
                  "",
                  "yuu",
                ].includes(memberFormState.heard_about_us) && (
                  <BasicTextField
                    setState={setMemberFormState}
                    stateKey="other_heard_about_us"
                    label="Other *"
                    variant="outlined"
                    placeholder=""
                    type="string"
                    required={true}
                    defaultValue={memberFormState.other_heard_about_us}
                  />
                )}
              </SplitLayout>
            </Grid>
          </Grid>
          <Grid
            container
            item
            width={"100%"}
            borderBottom={theme.border?.smallGrey}
            pt={2}
            pb={2}
          >
            <SplitLayout columns={[4, 4, 4]}>
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                defaultValue={memberFormState?.vehicleDetails[0]?.iuNumber}
                variant="outlined"
                label="IU Number 1"
                align="left"
                disableSubmit={setDisableSubmit}
                readonly={!!memberFormState?.vehicleDetails[0]?.iuNumber}
                stateKey="iuNumber1"
              />
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                required={!!memberFormState?.vehicleDetails[0]?.iuNumber}
                stateKey="licensePlate1"
                variant="outlined"
                label="License Plate 1"
                align="left"
                disableSubmit={setDisableSubmit}
                defaultValue={
                  memberFormState?.vehicleDetails[0]?.carPlateNumber
                }
                readonly={!!memberFormState?.vehicleDetails[0]?.carPlateNumber}
              />
            </SplitLayout>
            <SplitLayout columns={[4, 4, 4]}>
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                stateKey="iuNumber2"
                variant="outlined"
                label="IU Number 2"
                align="left"
                disableSubmit={setDisableSubmit}
                defaultValue={memberFormState?.vehicleDetails[1]?.iuNumber}
                readonly={!!memberFormState?.vehicleDetails[1]?.iuNumber}
              />
              <BasicTextField
                setState={setMemberFormState}
                placeholder=""
                required={!!memberFormState?.iuNumber2}
                stateKey="licensePlate2"
                variant="outlined"
                label="License Plate 2"
                align="left"
                disableSubmit={setDisableSubmit}
                defaultValue={
                  memberFormState?.vehicleDetails[1]?.carPlateNumber
                }
                readonly={!!memberFormState?.vehicleDetails[1]?.carPlateNumber}
              />
            </SplitLayout>
          </Grid>
          <Grid
            item
            width={"100%"}
            pt={2}
            pb={2}
            borderBottom={theme.border?.smallGrey}
          >
            <SplitLayout columns={[6]}>
              <Grid>
                <FormLabel
                  sx={{
                    ...formStyles.formLabel,
                    textAlign: "left",
                  }}
                >
                  Communication Preferences
                </FormLabel>

                <CheckBoxSection
                  setState={setMemberFormState}
                  direction="column"
                  items={[
                    {
                      stateKey: "subscr_for_news",
                      value: "Email",
                      checked: convertToBoolean(
                        memberFormState?.subscr_for_news,
                      ),
                    },
                    {
                      stateKey: "subscr_for_mkt",
                      value: "Push Notification",
                      checked: convertToBoolean(
                        memberFormState?.subscr_for_mkt,
                      ),
                    },
                  ]}
                />
              </Grid>
            </SplitLayout>
          </Grid>
          <Grid item width={"100%"}></Grid>

          <Grid item container width={"100%"} pt={3}>
            <Grid item width={"100%"} display={"flex"} justifyContent={"end"}>
              <Button
                onClick={closeUpdate ? () => closeUpdate(false) : () => {}}
                sx={{
                  ...buttonStyles.outlinedSecondary,
                  width: "25%",
                  mr: 4,
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={disableSubmit || checkDisableMobileSubmit()}
                type="submit"
                sx={{
                  ...buttonStyles.containedSecondary,
                  width: "25%",
                  marginRight: "1rem",
                }}
                variant="contained"
              >
                {type} Member
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {showModal && (
          <BasicModal
            title={messageToSuccess}
            success={!uploadError}
            setState={setShowModal}
          />
        )}
        {loading && <BasicModal content={<Loader />} />}
      </Grid>
    </Grid>
  );
};

export default UpdateMember;
